<template>
  <CModal
    v-model:visible="showModal"
    centered
    size="lg"
    :close-on-backdrop="false"
    @close="closeModal"
  >
    <template #header-wrapper>
      <div class="modal-header-custom p-3 border-bottom text-center w-100">
        <i class="fas fa-cloud-upload-alt fa-2x text-primary mb-2" />
        <h4 class="font-weight-bold mb-0">Upload Enterprise Build</h4>
        <p class="text-muted small mb-0">Upload an APK and publish a new version</p>
      </div>
    </template>

    <CCardBody class="p-4">
      <div v-if="!isUploading">
        <label class="font-weight-bold small text-uppercase text-muted mb-2">APK File</label>
        <div
          class="file-drop-zone"
          :class="{
            'file-selected': file,
            'drag-over': isDragging,
            'border-danger': extensionError,
          }"
          @click="triggerFileSelect"
          @dragover.prevent="isDragging = true"
          @dragleave.prevent="isDragging = false"
          @drop.prevent="handleFileDrop"
        >
          <div v-if="!file" class="text-center py-4">
            <i
              class="fas fa-file-upload fa-2x mb-2"
              :class="extensionError ? 'text-danger' : 'text-secondary'"
            />
            <p class="mb-0 small" :class="{ 'text-danger': extensionError }">
              {{
                extensionError
                  ? 'Invalid file type. Please select a .apk file'
                  : 'Click or drag your APK file here'
              }}
            </p>
            <p class="mb-0 text-muted" style="font-size: 0.75rem">.apk format</p>
          </div>
          <div v-else class="text-center py-2 w-100">
            <i class="fas fa-file-archive fa-2x mb-2 text-success" />
            <p
              class="mb-1 font-weight-bold text-truncate d-block mx-auto px-3"
              style="max-width: 300px"
              :title="file.name"
            >
              {{ file.name }}
            </p>
            <span class="badge badge-light border">{{ formatBytes(file.size) }}</span>
          </div>
        </div>

        <input ref="fileInput" type="file" class="d-none" accept=".apk" @change="onFileChange" />

        <div v-if="isParsing" class="mt-3 text-center py-3">
          <CSpinner color="primary" size="sm" class="mr-2" />
          <strong>Reading APK data...</strong>
        </div>

        <div v-if="apkInfo" class="apk-info-card mt-3 p-3 rounded">
          <div class="apk-info-row">
            <span class="apk-info-label">
              <i class="fas fa-tag mr-1" />
              Version
            </span>
            <span class="apk-info-value">{{ apkInfo.version }}</span>
          </div>
          <div class="apk-info-row">
            <span class="apk-info-label">
              <i class="fas fa-hashtag mr-1" />
              Build (Code)
            </span>
            <span class="apk-info-value">{{ apkInfo.build }}</span>
          </div>
          <div class="apk-info-row">
            <span class="apk-info-label">
              <i class="fas fa-cube mr-1" />
              Package
            </span>
            <span class="apk-info-value text-truncate" style="max-width: 60%">
              {{ apkInfo.package }}
            </span>
          </div>
        </div>
      </div>

      <div v-else class="text-center py-4">
        <div class="icon-pulse mb-3">
          <i class="fas fa-cloud-upload-alt fa-3x text-primary" />
        </div>
        <h5 class="mb-1">
          {{ statusMessage }}
        </h5>
        <p class="text-muted small mb-3">
          {{ file ? file.name : '' }}
        </p>
        <CProgress
          :value="uploadProgress"
          color="success"
          striped
          animated
          class="mb-2 mx-auto"
          style="height: 10px; max-width: 400px"
        />
        <span class="font-weight-bold text-success h4">{{ uploadProgress }}%</span>
      </div>

      <CAlert v-if="errorMessage" color="danger" class="mt-3 mb-0">
        <i class="fas fa-exclamation-triangle mr-1" />
        {{ errorMessage }}
      </CAlert>
    </CCardBody>

    <template #footer-wrapper>
      <div class="p-3 border-top d-flex justify-content-between bg-light">
        <CButton color="secondary" @click="cancel">
          <i class="fas fa-times mr-1" />
          Cancel
        </CButton>
        <CButton
          color="primary"
          :disabled="!file || !apkInfo || isUploading || isParsing"
          @click="submit"
        >
          <i class="fas fa-cloud-upload-alt mr-1" />
          Upload
        </CButton>
      </div>
    </template>
  </CModal>
</template>

<script>
import axios from 'axios';
import AppInfoParser from 'app-info-parser/dist/app-info-parser.js';
import { useToast } from 'vue-toastification';

export default {
  name: 'UploadBuildModal',
  emits: ['saved'],
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      showModal: false,
      file: null,
      apkInfo: null,
      isParsing: false,
      isUploading: false,
      uploadProgress: 0,
      statusMessage: '',
      errorMessage: '',
      isDragging: false,
      extensionError: false,
      uploadController: null,
    };
  },
  methods: {
    open() {
      this.reset();
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.reset();
    },
    reset() {
      this.file = null;
      this.apkInfo = null;
      this.isParsing = false;
      this.isUploading = false;
      this.uploadProgress = 0;
      this.statusMessage = '';
      this.errorMessage = '';
      this.isDragging = false;
      this.extensionError = false;
      this.uploadController = null;
    },
    cancel() {
      if (this.uploadController) {
        this.uploadController.abort();
      }
      this.closeModal();
    },
    triggerFileSelect() {
      const input = this.$refs.fileInput;
      if (input) input.click();
    },
    handleFileDrop(e) {
      this.isDragging = false;
      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        this.processFile(files[0]);
      }
    },
    onFileChange(event) {
      const files = event.target ? event.target.files : null;
      const file = files && files.length > 0 ? files[0] : null;
      this.processFile(file);
      if (event.target) event.target.value = '';
    },
    processFile(file) {
      if (!file) {
        this.file = null;
        this.apkInfo = null;
        return;
      }

      const ext = file.name.split('.').pop().toLowerCase();
      if (ext !== 'apk') {
        this.file = null;
        this.apkInfo = null;
        this.extensionError = true;
        this.$toast.error('File not valid. Please select a .apk build');
        return;
      }

      this.extensionError = false;
      this.file = file;
      this.apkInfo = null;
      this.errorMessage = '';
      this.readApkInfo(file);
    },
    async readApkInfo(file) {
      this.isParsing = true;

      try {
        // Parse the APK locally when selected
        const parser = new AppInfoParser(file);
        const result = await parser.parse();

        // Ignore stale results if the modal was reset/closed meanwhile
        if (this.file !== file) return;

        this.apkInfo = {
          version: result.versionName,
          build: result.versionCode,
          package: result.package,
        };
      } catch (error) {
        console.error('Error parsing APK:', error);
        if (this.file !== file) return;
        this.errorMessage = 'Could not read the APK file. Please ensure it is a valid file.';
        this.file = null;
        this.apkInfo = null;
      } finally {
        this.isParsing = false;
      }
    },
    formatBytes(bytes, decimals = 2) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const dm = decimals < 0 ? 0 : decimals;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
    },
    async submit() {
      if (!this.file || !this.apkInfo) return;

      this.isUploading = true;
      this.uploadProgress = 0;
      this.errorMessage = '';
      const controller = new AbortController();
      this.uploadController = controller;

      try {
        // STEP 1: Request presigned URL from backend
        this.statusMessage = 'Preparing upload...';
        const { data: presignedData } = await this.$http.post('/app-versions/generate-upload-url', {
          filename: this.file.name,
        });

        // STEP 2: Upload directly to S3 (clean axios instance to avoid auth headers that break the AWS signature)
        this.statusMessage = 'Uploading file to S3...';
        const cleanAxios = axios.create();
        await cleanAxios.put(presignedData.upload_url, this.file, {
          headers: { 'Content-Type': 'application/vnd.android.package-archive' },
          signal: controller.signal,
          onUploadProgress: (progressEvent) => {
            this.uploadProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          },
        });

        // STEP 3: Confirm upload and send APK data to backend
        this.statusMessage = 'Confirming and saving version...';
        await this.$http.post('/app-versions/confirm-upload', {
          path: presignedData.path,
          original_name: presignedData.original_name,
          version: this.apkInfo.version,
          build: this.apkInfo.build,
          package: this.apkInfo.package,
        });
        this.$toast.success('Upload completed!');
        this.$emit('saved');
        this.closeModal();
      } catch (error) {
        if (axios.isCancel(error)) {
          return;
        }

        console.error('Upload process failed:', error);

        if (error.response && error.response.data) {
          this.errorMessage =
            error.response.data.error ||
            error.response.data.message ||
            'An unexpected error occurred.';
        } else {
          this.errorMessage =
            error.message || 'An unexpected error occurred while uploading the file.';
        }
      } finally {
        this.isUploading = false;
        this.uploadController = null;
      }
    },
  },
};
</script>

<style scoped>
.file-drop-zone {
  border: 2px dashed #d8dbe0;
  border-radius: 8px;
  background-color: #f8f9fa;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  min-height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.file-drop-zone:hover {
  background-color: #f0f3f5;
  border-color: #321fdb;
}
.file-drop-zone.drag-over {
  border-color: #2eb85c;
  background-color: #ebf9f1;
  transform: scale(1.02);
}
.file-drop-zone.file-selected {
  border: 2px solid #2eb85c;
  background-color: #f0fff4;
}
.file-drop-zone.border-danger {
  border-color: #e55353 !important;
  background-color: #fff5f5;
}
.apk-info-card {
  background-color: #f0f3f5;
  border: 1px solid #d8dbe0;
}
.apk-info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.4rem 0.75rem;
}
.apk-info-row + .apk-info-row {
  border-top: 1px solid #e4e7ea;
}
.apk-info-label {
  color: #6c757d;
  font-size: 0.875rem;
  font-weight: 600;
}
.apk-info-value {
  font-weight: 700;
  color: #321fdb;
  word-break: break-all;
}
@keyframes pulse-blue {
  0% {
    transform: scale(0.95);
    opacity: 0.7;
  }
  50% {
    transform: scale(1.05);
    opacity: 1;
  }
  100% {
    transform: scale(0.95);
    opacity: 0.7;
  }
}
.icon-pulse {
  animation: pulse-blue 2s infinite ease-in-out;
}
.card-body {
  background-color: #fff;
  color: var(--cui-card-color, var(--cui-body-color, inherit));
}
</style>
